import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useFamilyContext } from '../contexts/FamilyContext';

export function usePacking() {
  const { currentUser } = useFamilyContext();
  const currentMember = currentUser; // For consistency with other hooks
  const [loading, setLoading] = useState(false);
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [favorites, setFavorites] = useState([]);

  // Fetch all packing lists for current user
  const fetchLists = useCallback(async () => {
    if (!currentMember?.id) return;

    try {
      setLoading(true);
      console.log('🎒 Fetching packing lists for user:', currentMember.id, 'Name:', currentMember.name, 'Type:', currentMember.type);
      const { data: lists, error: listsError } = await supabase
        .from('packing_lists')
        .select(`
          *,
          items:packing_items(*),
          collaborators:packing_list_collaborators(
            id,
            collaborator_id,
            can_edit,
            can_pack_items,
            collaborator:family_members(*)
          )
        `)
        .eq('is_template', false)
        .eq('owner_id', currentMember.id);
      
      if (listsError) {
        console.error('🎒 Error fetching user lists:', listsError);
        throw listsError;
      }
      
      console.log('🎒 Found lists for current user:', lists?.length || 0);
      
      // Debug weather data in fetched lists
      if (lists && lists.length > 0) {
        lists.forEach((list, index) => {
          console.log(`🌤️ Weather Debug - Fetched List ${index + 1}:`, {
            id: list.id,
            name: list.name,
            weather_expected: list.weather_expected,
            weather_data: list.weather_data,
            trip_legs: list.trip_legs,
            destination: list.destination
          });
        });
      }

      // Also fetch lists where user is explicitly a collaborator
      const { data: collaborativeListIds, error: collabError } = await supabase
        .from('packing_list_collaborators')
        .select('list_id')
        .eq('collaborator_id', currentMember.id);

      if (collabError) throw collabError;

      let collaborativeLists = [];
      if (collaborativeListIds && collaborativeListIds.length > 0) {
        const listIds = collaborativeListIds.map(c => c.list_id);
        const { data: collabLists, error: collabListsError } = await supabase
          .from('packing_lists')
          .select(`
            *,
            items:packing_items(*),
            collaborators:packing_list_collaborators(
              id,
              collaborator_id,
              can_edit,
              can_pack_items,
              collaborator:family_members(*)
            )
          `)
          .eq('is_template', false)
          .in('id', listIds);
        
        if (collabListsError) throw collabListsError;
        collaborativeLists = collabLists || [];
      }

      // Combine and deduplicate
      const allListsMap = new Map();
      [...(lists || []), ...collaborativeLists].forEach(list => {
        allListsMap.set(list.id, list);
      });
      
      const data = Array.from(allListsMap.values()).sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setLists(data || []);
    } catch (error) {
      console.error('Error fetching packing lists:', error);
    } finally {
      setLoading(false);
    }
  }, [currentMember?.id]);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('packing_templates')
        .select('*')
        .order('name');

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  // Fetch user's packing favorites
  const fetchFavorites = useCallback(async () => {
    if (!currentMember?.id) return;

    try {
      const { data, error } = await supabase
        .from('packing_favorites')
        .select('*')
        .eq('owner_id', currentMember.id)
        .order('frequency_score', { ascending: false });

      if (error) throw error;
      setFavorites(data || []);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  }, [currentMember?.id]);

  // Create a new packing list
  const createList = async (listData) => {
    if (!currentMember?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('packing_lists')
        .insert({
          ...listData,
          owner_id: currentMember.id
        })
        .select()
        .single();

      if (error) throw error;
      
      // Auto-create collaborator records
      await createCollaboratorsForList(data.id, currentMember.id);
      
      await fetchLists();
      return data;
    } catch (error) {
      console.error('Error creating packing list:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Helper function to create collaborator records for a new list
  const createCollaboratorsForList = async (listId, ownerId) => {
    try {
      // Always add the owner as a collaborator
      const collaboratorsToAdd = [
        {
          list_id: listId,
          collaborator_id: ownerId,
          can_edit: true,
          can_pack_items: true
        }
      ];

      // If the owner is a kid/student, also add parents as collaborators
      if (currentMember.type === 'student' || currentMember.type === 'kid') {
        // Get all parents in the family
        const { data: parents, error: parentsError } = await supabase
          .from('family_members')
          .select('id')
          .eq('user_type', 'parent');

        if (!parentsError && parents) {
          parents.forEach(parent => {
            if (parent.id !== ownerId) { // Don't duplicate the owner
              collaboratorsToAdd.push({
                list_id: listId,
                collaborator_id: parent.id,
                can_edit: true,
                can_pack_items: true
              });
            }
          });
        }
      }

      // Insert all collaborator records
      const { error: collabError } = await supabase
        .from('packing_list_collaborators')
        .insert(collaboratorsToAdd);

      if (collabError) {
        console.error('Error creating collaborator records:', collabError);
      } else {
        console.log('✅ Created collaborator records for new list');
      }
    } catch (error) {
      console.error('Error in createCollaboratorsForList:', error);
    }
  };

  // Create list from template
  const createFromTemplate = async (templateId, listData) => {
    if (!currentMember?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase.rpc(
        'create_packing_list_from_template',
        {
          p_template_id: templateId,
          p_owner_id: currentMember.id,
          p_name: listData.name,
          p_destination: listData.destination,
          p_start_date: listData.start_date,
          p_end_date: listData.end_date
        }
      );

      if (error) throw error;
      
      // Auto-create collaborator records for template-based lists too
      if (data?.id) {
        await createCollaboratorsForList(data.id, currentMember.id);
      }
      
      await fetchLists();
      return data;
    } catch (error) {
      console.error('Error creating list from template:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update a packing list
  const updateList = async (listId, updates) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('packing_lists')
        .update(updates)
        .eq('id', listId);

      if (error) throw error;
      
      await fetchLists();
    } catch (error) {
      console.error('Error updating packing list:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete a packing list
  const deleteList = async (listId) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('packing_lists')
        .delete()
        .eq('id', listId);

      if (error) throw error;
      
      await fetchLists();
    } catch (error) {
      console.error('Error deleting packing list:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Add item to list
  const addItem = async (listId, item) => {
    try {
      const { error } = await supabase
        .from('packing_items')
        .insert({
          list_id: listId,
          ...item
        });

      if (error) throw error;
      
      await fetchLists();
    } catch (error) {
      console.error('Error adding item:', error);
      throw error;
    }
  };

  // Update item
  const updateItem = async (itemId, updates) => {
    try {
      const { error } = await supabase
        .from('packing_items')
        .update(updates)
        .eq('id', itemId);

      if (error) throw error;
      
      await fetchLists();
    } catch (error) {
      console.error('Error updating item:', error);
      throw error;
    }
  };

  // Delete item
  const deleteItem = async (itemId) => {
    try {
      const { error } = await supabase
        .from('packing_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
      
      await fetchLists();
    } catch (error) {
      console.error('Error deleting item:', error);
      throw error;
    }
  };

  // Toggle item packed status
  const toggleItemPacked = async (itemId) => {
    if (!currentMember?.id) return;

    try {
      const { error } = await supabase.rpc(
        'pack_item',
        {
          p_item_id: itemId,
          p_packed_by: currentMember.id
        }
      );

      if (error) throw error;
      
      await fetchLists();
    } catch (error) {
      console.error('Error toggling packed status:', error);
      throw error;
    }
  };

  // Add item to favorites
  const addToFavorites = async (itemName, category, tripType = null) => {
    if (!currentMember?.id) return;

    try {
      const { error } = await supabase.rpc(
        'add_to_packing_favorites',
        {
          p_owner_id: currentMember.id,
          p_item_name: itemName,
          p_category: category,
          p_trip_type: tripType
        }
      );

      if (error) throw error;
      
      await fetchFavorites();
    } catch (error) {
      console.error('Error adding to favorites:', error);
      throw error;
    }
  };

  // Add collaborator to list
  const addCollaborator = async (listId, collaboratorId, permissions = {}) => {
    try {
      const { error } = await supabase
        .from('packing_list_collaborators')
        .insert({
          list_id: listId,
          collaborator_id: collaboratorId,
          can_edit: permissions.canEdit ?? true,
          can_pack_items: permissions.canPackItems ?? true
        });

      if (error) throw error;
      
      await fetchLists();
    } catch (error) {
      console.error('Error adding collaborator:', error);
      throw error;
    }
  };

  // Remove collaborator
  const removeCollaborator = async (listId, collaboratorId) => {
    try {
      const { error } = await supabase
        .from('packing_list_collaborators')
        .delete()
        .eq('list_id', listId)
        .eq('collaborator_id', collaboratorId);

      if (error) throw error;
      
      await fetchLists();
    } catch (error) {
      console.error('Error removing collaborator:', error);
      throw error;
    }
  };

  // Save list as template
  const saveAsTemplate = async (listId, templateName, description) => {
    if (!currentMember?.id) return;

    try {
      setLoading(true);
      
      // Get the list with items
      const { data: list, error: listError } = await supabase
        .from('packing_lists')
        .select('*, items:packing_items(*)')
        .eq('id', listId)
        .single();

      if (listError) throw listError;

      // Create template list
      const { data: template, error: templateError } = await supabase
        .from('packing_lists')
        .insert({
          name: templateName || `${list.name} Template`,
          description: description || list.description,
          destination: null,
          trip_type: list.trip_type,
          weather_expected: list.weather_expected,
          transportation_mode: list.transportation_mode,
          is_template: true,
          owner_id: currentMember.id,
          status: 'planning'
        })
        .select()
        .single();

      if (templateError) throw templateError;

      // Copy items to template
      if (list.items && list.items.length > 0) {
        const templateItems = list.items.map(item => ({
          list_id: template.id,
          name: item.name,
          quantity: item.quantity,
          notes: item.notes,
          category: item.category,
          luggage_type: item.luggage_type
        }));

        const { error: itemsError } = await supabase
          .from('packing_items')
          .insert(templateItems);

        if (itemsError) throw itemsError;
      }

      await fetchLists();
      return template;
    } catch (error) {
      console.error('Error saving as template:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get suggested items based on trip type and past favorites
  const getSuggestedItems = useCallback(async (tripType) => {
    if (!currentMember?.id) return [];

    try {
      // Get favorites that match trip type
      const { data: matchingFavorites, error } = await supabase
        .from('packing_favorites')
        .select('*')
        .eq('owner_id', currentMember.id)
        .or(`trip_types.cs.{${tripType}},trip_types.eq.{}`)
        .order('frequency_score', { ascending: false })
        .limit(20);

      if (error) throw error;

      return matchingFavorites || [];
    } catch (error) {
      console.error('Error getting suggested items:', error);
      return [];
    }
  }, [currentMember?.id]);

  // Initial data fetch
  useEffect(() => {
    if (currentMember?.id) {
      fetchLists();
      fetchTemplates();
      fetchFavorites();
    }
  }, [currentMember?.id, fetchLists, fetchTemplates, fetchFavorites]);

  return {
    loading,
    lists,
    templates,
    favorites,
    createList,
    createFromTemplate,
    updateList,
    deleteList,
    addItem,
    updateItem,
    deleteItem,
    toggleItemPacked,
    addToFavorites,
    addCollaborator,
    removeCollaborator,
    saveAsTemplate,
    getSuggestedItems,
    refetch: fetchLists
  };
}